import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import {
  BarChart3 as BarChartLogoIcon,
  Calendar as CalendarIcon,
  CheckCircle2 as CheckCircleIcon,
  Edit as EditIcon,
  FileText as FileTextIcon,
  Plus as PlusIcon,
  RefreshCw,
  Search as SearchIcon,
  Sparkles,
  Trash2 as DeleteIcon,
  Users as UsersIcon
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useJob } from '../context/JobContext';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
  height: '100%'
}));

const StatWidget = ({ title, value, icon: Icon, color, subtitle, subtitleColor, helperText }) => (
  <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', height: '100%' }} elevation={0} variant="outlined">
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color: color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" sx={{ mt: 0.25, color: subtitleColor || 'text.secondary' }}>
          {subtitle}
        </Typography>
      ) : null}
      {helperText ? (
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      ) : null}
    </Box>
  </Paper>
);

const extractYmd = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  const ymd = str.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
  const date = new Date(str);
  if (!Number.isFinite(date.getTime())) return '';
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateIt = (value) => {
  const ymd = extractYmd(value);
  if (!ymd) return '-';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
};

const formatNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('it-IT');
};

const formatNumber2 = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.00';
  return (Math.round(n * 100) / 100).toFixed(2);
};

const formatDelta = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toLocaleString('it-IT')}`;
};

const formatDeltaPct = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};

const getCurrentMonthRangeYmd = () => {
  const now = new Date();
  const year = now.getFullYear();
  const monthIndex = now.getMonth();
  const first = new Date(year, monthIndex, 1);
  const last = new Date(year, monthIndex + 1, 0);
  const pad2 = (v) => String(v).padStart(2, '0');
  const from = `${first.getFullYear()}-${pad2(first.getMonth() + 1)}-${pad2(first.getDate())}`;
  const to = `${last.getFullYear()}-${pad2(last.getMonth() + 1)}-${pad2(last.getDate())}`;
  return { from, to };
};

const defaultForm = {
  data_riferimento: '',
  operatore_id: '',
  mattina_lead_assegnati: 0,
  mattina_lead_confermati: 0,
  pomeriggio_lead_assegnati: 0,
  pomeriggio_lead_confermati: 0,
  calcolo_particolare: false,
  motivo_calcolo_particolare: ''
};

export default function KpiLeadPage() {
  const { user } = useAuth();
  const { addJob } = useJob();
  const roleName = String(user?.role_name || user?.role || '').toLowerCase();
  const isAdmin = roleName === 'admin';

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const [operatori, setOperatori] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState('data_riferimento');
  const [orderDirection, setOrderDirection] = useState('desc');

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(() => {
    const { from, to } = getCurrentMonthRangeYmd();
    return {
      operatore_id: '',
      date_from: from,
      date_to: to,
      calcolo_particolare: '',
      bonus_only: false
    };
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [current, setCurrent] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [error, setError] = useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportYear, setReportYear] = useState(() => new Date().getFullYear());
  const [reportOrder, setReportOrder] = useState('total_desc');
  const [reportStarting, setReportStarting] = useState(false);

  const fetchMeta = async () => {
    try {
      const res = await api.get('/kpi-lead/appuntamenti/meta');
      setOperatori(Array.isArray(res.data?.operatori) ? res.data.operatori : []);
    } catch (e) {
      console.error('Error fetching KPI lead meta:', e);
    }
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await api.get('/kpi-lead/appuntamenti', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search,
          orderBy,
          orderDirection,
          ...filters,
          bonus_only: filters.bonus_only ? 1 : 0
        }
      });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotal(Number(res.data?.meta?.total || 0));
      setStats(res.data?.stats || null);
      setError('');
    } catch (err) {
      console.error('Error fetching KPI lead rows:', err);
      setError('Errore nel caricamento dei KPI');
      toast.error('Errore nel caricamento dei KPI');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeta();
  }, []);

  useEffect(() => {
    fetchRows();
  }, [page, rowsPerPage, orderBy, orderDirection, search, filters]);

  const operatoreOptions = useMemo(() => {
    return [...operatori]
      .map((o) => ({ id: String(o.id), label: o.name || `Utente ${o.id}` }))
      .sort((a, b) => a.label.localeCompare(b.label, 'it', { sensitivity: 'base' }));
  }, [operatori]);

  const selectedOperatore = operatoreOptions.find((o) => o.id === String(filters.operatore_id || '')) || null;
  const selectedOperatoreForm = operatoreOptions.find((o) => o.id === String(formData.operatore_id || '')) || null;

  const createSortHandler = (property) => () => {
    const isAsc = orderBy === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (row = null) => {
    if (row) {
      setCurrent(row);
      setFormData({
        data_riferimento: extractYmd(row.data_riferimento),
        operatore_id: String(row.operatore_id || ''),
        mattina_lead_assegnati: Number(row.mattina_lead_assegnati || 0),
        mattina_lead_confermati: Number(row.mattina_lead_confermati || 0),
        pomeriggio_lead_assegnati: Number(row.pomeriggio_lead_assegnati || 0),
        pomeriggio_lead_confermati: Number(row.pomeriggio_lead_confermati || 0),
        calcolo_particolare: Boolean(row.calcolo_particolare),
        motivo_calcolo_particolare: row.motivo_calcolo_particolare || ''
      });
    } else {
      setCurrent(null);
      setFormData(defaultForm);
    }
    setError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrent(null);
    setError('');
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        data_riferimento: extractYmd(formData.data_riferimento),
        operatore_id: Number(formData.operatore_id),
        mattina_lead_assegnati: Number(formData.mattina_lead_assegnati),
        mattina_lead_confermati: Number(formData.mattina_lead_confermati),
        pomeriggio_lead_assegnati: Number(formData.pomeriggio_lead_assegnati),
        pomeriggio_lead_confermati: Number(formData.pomeriggio_lead_confermati),
        calcolo_particolare: Boolean(formData.calcolo_particolare),
        motivo_calcolo_particolare: String(formData.motivo_calcolo_particolare || '')
      };

      if (current?.id) {
        await api.put(`/kpi-lead/appuntamenti/${current.id}`, payload);
        toast.success('KPI aggiornato con successo');
      } else {
        await api.post('/kpi-lead/appuntamenti', payload);
        toast.success('KPI creato con successo');
      }

      await fetchMeta();
      await fetchRows();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving KPI lead row:', err);
      const message = err.response?.data?.message || 'Errore nel salvataggio';
      setError(message);
      toast.error(message);
    }
  };

  const handleStartYearlyReport = async () => {
    const year = Number(reportYear);
    if (!Number.isInteger(year) || year < 2000 || year > 2100) {
      toast.error('Anno non valido');
      return;
    }
    setReportStarting(true);
    try {
      const res = await api.post('/kpi-lead/appuntamenti/report-job', { year, order: reportOrder });
      const jobId = res.data?.jobId;
      if (!jobId) {
        throw new Error('jobId mancante');
      }
      addJob(jobId, 'kpi_lead_yearly_pdf');
      setReportDialogOpen(false);
    } catch (err) {
      console.error('Error starting KPI Lead yearly report job:', err);
      const message = err.response?.data?.message || 'Errore avvio generazione PDF';
      toast.error(message);
    } finally {
      setReportStarting(false);
    }
  };

  const openDeleteDialog = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/kpi-lead/appuntamenti/${deleteTarget.id}`);
      toast.success('KPI eliminato');
      closeDeleteDialog();
      await fetchRows();
    } catch (err) {
      console.error('Error deleting KPI lead row:', err);
      toast.error('Errore durante l’eliminazione');
    } finally {
      setDeleteLoading(false);
    }
  };

  const CalcoloChip = ({ value }) => {
    const active = Boolean(value);
    return (
      <Chip
        size="small"
        label={active ? 'Si' : 'No'}
        color={active ? 'warning' : 'default'}
        variant={active ? 'filled' : 'outlined'}
      />
    );
  };

  const BonusChip = ({ value }) => {
    const active = Boolean(Number(value));
    return (
      <Chip
        size="small"
        label={active ? 'Bonus' : '—'}
        color={active ? 'success' : 'default'}
        variant={active ? 'filled' : 'outlined'}
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <BarChartLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              KPI Lead Giornalieri
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Inserimento e consultazione KPI giornalieri per operatore
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshCw size={18} />} onClick={fetchRows} disabled={loading}>
            Aggiorna
          </Button>
          {isAdmin && (
            <Button
              variant="outlined"
              startIcon={<FileTextIcon size={18} />}
              onClick={() => setReportDialogOpen(true)}
              disabled={loading}
            >
              Report PDF
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<PlusIcon size={18} />}
            onClick={() => handleOpenDialog(null)}
          >
            Nuovo KPI
          </Button>
        </Box>
      </Box>

      {stats && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            {(() => {
              const prev = stats?.prev_month;
              const deltaAbs = prev?.delta_assegnati_abs;
              const deltaPct = prev?.delta_assegnati_pct;
              const hasPrev = prev && (Number(prev.assegnati_totali_sum || 0) > 0 || Number(stats.assegnati_totali_sum || 0) > 0);
              const isNeg = Number(deltaAbs || 0) < 0;
              const subtitle = hasPrev
                ? `${formatDelta(deltaAbs)} (${deltaPct === null ? '—' : formatDeltaPct(deltaPct)}) vs mese precedente`
                : 'Nessun dato precedente';
              const subtitleColor = hasPrev ? (isNeg ? '#ef4444' : '#22c55e') : 'text.secondary';
              return (
                <StatWidget
                  title="Lead Assegnati (Mese)"
                  value={formatNumber(stats.assegnati_totali_sum)}
                  icon={UsersIcon}
                  color="#3b82f6"
                  subtitle={subtitle}
                  subtitleColor={subtitleColor}
                />
              );
            })()}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {(() => {
              const prev = stats?.prev_month;
              const deltaAbs = prev?.delta_confermati_abs;
              const deltaPct = prev?.delta_confermati_pct;
              const hasPrev = prev && (Number(prev.confermati_totali_sum || 0) > 0 || Number(stats.confermati_totali_sum || 0) > 0);
              const isNeg = Number(deltaAbs || 0) < 0;
              const subtitle = hasPrev
                ? `${formatDelta(deltaAbs)} (${deltaPct === null ? '—' : formatDeltaPct(deltaPct)}) vs mese precedente`
                : 'Nessun dato precedente';
              const subtitleColor = hasPrev ? (isNeg ? '#ef4444' : '#22c55e') : 'text.secondary';
              return (
                <StatWidget
                  title="Appuntamenti Confermati"
                  value={formatNumber(stats.confermati_totali_sum)}
                  icon={CalendarIcon}
                  color="#4f46e5"
                  subtitle={subtitle}
                  subtitleColor={subtitleColor}
                />
              );
            })()}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatWidget
              title="Operatori Bonus"
              value={formatNumber(stats.operatori_bonus_count)}
              icon={Sparkles}
              color="#f59e0b"
              subtitle="≥ 40 appuntamenti confermati"
              subtitleColor="#22c55e"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatWidget
              title="Media Giornaliera"
              value={formatNumber2(stats.media_giornaliera_lavorativa)}
              icon={CalendarIcon}
              color="#22c55e"
              subtitle="Appuntamenti per giorno lavorativo"
              subtitleColor="#22c55e"
            />
          </Grid>
        </Grid>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <StyledCard>
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                placeholder="Cerca per data, operatore, motivo..."
                variant="outlined"
                size="small"
                fullWidth
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon size={20} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                size="small"
                options={operatoreOptions}
                value={selectedOperatore}
                onChange={(_event, newValue) => {
                  setFilters((prev) => ({ ...prev, operatore_id: newValue?.id ? String(newValue.id) : '' }));
                  setPage(0);
                }}
                getOptionLabel={(opt) => opt?.label || ''}
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                renderInput={(params) => <TextField {...params} label="Operatore" />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                type="date"
                size="small"
                fullWidth
                label="Da"
                InputLabelProps={{ shrink: true }}
                value={filters.date_from}
                onChange={(e) => {
                  setFilters((prev) => ({ ...prev, date_from: e.target.value }));
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                type="date"
                size="small"
                fullWidth
                label="A"
                InputLabelProps={{ shrink: true }}
                value={filters.date_to}
                onChange={(e) => {
                  setFilters((prev) => ({ ...prev, date_to: e.target.value }));
                  setPage(0);
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Calcolo</InputLabel>
                <Select
                  value={filters.calcolo_particolare}
                  label="Calcolo"
                  onChange={(e) => {
                    setFilters((prev) => ({ ...prev, calcolo_particolare: e.target.value }));
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Tutti</MenuItem>
                  <MenuItem value="true">Si</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Bonus</InputLabel>
                <Select
                  value={filters.bonus_only ? 'true' : 'false'}
                  label="Bonus"
                  onChange={(e) => {
                    setFilters((prev) => ({ ...prev, bonus_only: String(e.target.value) === 'true' }));
                    setPage(0);
                  }}
                >
                  <MenuItem value="false">Tutti</MenuItem>
                  <MenuItem value="true">Solo bonus</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'data_riferimento'}
                      direction={orderBy === 'data_riferimento' ? orderDirection : 'asc'}
                      onClick={createSortHandler('data_riferimento')}
                    >
                      Data
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'operatore_nome'}
                      direction={orderBy === 'operatore_nome' ? orderDirection : 'asc'}
                      onClick={createSortHandler('operatore_nome')}
                    >
                      Operatore
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'mattina_lead_assegnati'}
                      direction={orderBy === 'mattina_lead_assegnati' ? orderDirection : 'asc'}
                      onClick={createSortHandler('mattina_lead_assegnati')}
                    >
                      Mattina Lead Assegnati
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'mattina_lead_confermati'}
                      direction={orderBy === 'mattina_lead_confermati' ? orderDirection : 'asc'}
                      onClick={createSortHandler('mattina_lead_confermati')}
                    >
                      Mattina Appuntamenti Confermati
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'pomeriggio_lead_assegnati'}
                      direction={orderBy === 'pomeriggio_lead_assegnati' ? orderDirection : 'asc'}
                      onClick={createSortHandler('pomeriggio_lead_assegnati')}
                    >
                      Pomeriggio Lead Assegnati
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'pomeriggio_lead_confermati'}
                      direction={orderBy === 'pomeriggio_lead_confermati' ? orderDirection : 'asc'}
                      onClick={createSortHandler('pomeriggio_lead_confermati')}
                    >
                      Pomeriggio Appuntamenti Confermati
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'assegnati_totali'}
                      direction={orderBy === 'assegnati_totali' ? orderDirection : 'asc'}
                      onClick={createSortHandler('assegnati_totali')}
                    >
                      Totale Lead Assegnati
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'confermati_totali'}
                      direction={orderBy === 'confermati_totali' ? orderDirection : 'asc'}
                      onClick={createSortHandler('confermati_totali')}
                    >
                      Totale Appuntamenti
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'percentuale_conversione'}
                      direction={orderBy === 'percentuale_conversione' ? orderDirection : 'asc'}
                      onClick={createSortHandler('percentuale_conversione')}
                    >
                      % Conversione
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={orderBy === 'eleggibile_bonus'}
                      direction={orderBy === 'eleggibile_bonus' ? orderDirection : 'asc'}
                      onClick={createSortHandler('eleggibile_bonus')}
                    >
                      Bonus
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={orderBy === 'calcolo_particolare'}
                      direction={orderBy === 'calcolo_particolare' ? orderDirection : 'asc'}
                      onClick={createSortHandler('calcolo_particolare')}
                    >
                      Speciale
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                      Nessun KPI trovato
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateIt(row.data_riferimento)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.operatore_nome || `Utente ${row.operatore_id}`}</TableCell>
                      <TableCell align="right">{row.mattina_lead_assegnati}</TableCell>
                      <TableCell align="right">{row.mattina_lead_confermati}</TableCell>
                      <TableCell align="right">{row.pomeriggio_lead_assegnati}</TableCell>
                      <TableCell align="right">{row.pomeriggio_lead_confermati}</TableCell>
                      <TableCell align="right">{row.assegnati_totali}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.confermati_totali}
                          </Typography>
                          {Boolean(row.calcolo_particolare) && (
                            <Typography variant="caption" color="text.secondary">
                              Ricalcolato: {formatNumber2(row.appuntamenti_ricalcolati)}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{formatNumber2(row.percentuale_conversione)}</TableCell>
                      <TableCell align="center">
                        <BonusChip value={row.eleggibile_bonus} />
                      </TableCell>
                      <TableCell align="center">
                        <CalcoloChip value={row.calcolo_particolare} />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            color="primary"
                            title="Modifica"
                            onClick={() => handleOpenDialog(row)}
                          >
                            <EditIcon size={18} />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            title="Elimina"
                            onClick={() => openDeleteDialog(row)}
                          >
                            <DeleteIcon size={18} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Righe per pagina"
          />
        </CardContent>
      </StyledCard>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{current ? 'Modifica KPI' : 'Nuovo KPI'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="data_riferimento"
                  label="Data riferimento"
                  type="date"
                  value={formData.data_riferimento}
                  onChange={handleFormChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  options={operatoreOptions}
                  value={selectedOperatoreForm}
                  onChange={(_event, newValue) => {
                    setFormData((prev) => ({ ...prev, operatore_id: newValue?.id ? String(newValue.id) : '' }));
                  }}
                  getOptionLabel={(opt) => opt?.label || ''}
                  isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                  renderInput={(params) => <TextField {...params} label="Operatore" />}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  name="mattina_lead_assegnati"
                  label="Mattina assegnati"
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  value={formData.mattina_lead_assegnati}
                  onChange={handleFormChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  name="mattina_lead_confermati"
                  label="Mattina confermati"
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  value={formData.mattina_lead_confermati}
                  onChange={handleFormChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  name="pomeriggio_lead_assegnati"
                  label="Pomeriggio assegnati"
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  value={formData.pomeriggio_lead_assegnati}
                  onChange={handleFormChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  name="pomeriggio_lead_confermati"
                  label="Pomeriggio confermati"
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  value={formData.pomeriggio_lead_confermati}
                  onChange={handleFormChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Calcolo particolare</InputLabel>
                  <Select
                    value={formData.calcolo_particolare ? 'true' : 'false'}
                    label="Calcolo particolare"
                    onChange={(e) => {
                      const active = String(e.target.value) === 'true';
                      setFormData((prev) => ({
                        ...prev,
                        calcolo_particolare: active,
                        motivo_calcolo_particolare: active ? prev.motivo_calcolo_particolare : ''
                      }));
                    }}
                  >
                    <MenuItem value="false">No</MenuItem>
                    <MenuItem value="true">Si</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  name="motivo_calcolo_particolare"
                  label="Motivo calcolo particolare"
                  value={formData.motivo_calcolo_particolare}
                  onChange={handleFormChange}
                  fullWidth
                  disabled={!formData.calcolo_particolare}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCloseDialog}>
            Annulla
          </Button>
          <Button variant="contained" onClick={handleSubmit}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Eliminare KPI {formatDateIt(deleteTarget?.data_riferimento)} — {deleteTarget?.operatore_nome || `Utente ${deleteTarget?.operatore_id}`}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeDeleteDialog} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteLoading}>
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Report KPI Lead Annuale</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Seleziona l’anno di riferimento. Il PDF sarà disponibile nelle notifiche al completamento.
          </Typography>
          <TextField
            label="Anno"
            type="number"
            fullWidth
            value={reportYear}
            onChange={(e) => setReportYear(e.target.value)}
            inputProps={{ min: 2000, max: 2100, step: 1 }}
          />
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Ordinamento</InputLabel>
            <Select
              value={reportOrder}
              label="Ordinamento"
              onChange={(e) => setReportOrder(String(e.target.value))}
            >
              <MenuItem value="total_desc">Totale annuale più alto</MenuItem>
              <MenuItem value="alpha">Alfabetico</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setReportDialogOpen(false)} disabled={reportStarting}>
            Annulla
          </Button>
          <Button variant="contained" onClick={handleStartYearlyReport} disabled={reportStarting}>
            {reportStarting ? 'Avvio...' : 'Genera PDF'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
